import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, BarChart3, TrendingUp, Users, School, Shield, FileText, Eye, GitBranch, Award, DollarSign, AlertCircle, CheckCircle, Target, Zap, MessageSquare, Layers, Lock } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageDirections = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Directions | Gouvernance"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="directions" lang="fr" />

      {/* Hero Directions */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Building2 size={16} />
            <span>Portail Directions & Commissions Scolaires</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-directions)' }}>Directions</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Supervisez <strong style={{ color: 'var(--actor-directions)' }}>tous vos établissements</strong><br />
            Vue consolidée • Gouvernance renforcée
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Écoles supervisées</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Vue consolidée</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Conformité assurée</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste — Pourquoi EcoLunch pour les Directions */}
      <section className="section" data-testid="manifeste-directions">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch pour les Directions ?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-gouvernance">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#8B5CF6'}}><Layers size={30} /></div>
              <h3>Gouvernance centralisée et visibilité totale</h3>
            </div>
            <p>Les directions d'établissement et les commissions scolaires Québécoises gèrent souvent plusieurs écoles avec des outils fragmentés. EcoLunch offre un <strong>portail de gouvernance unifié</strong> qui consolide toutes les données de participation, satisfaction et conformité nutritionnelle en temps réel.</p>
            <p>Un seul tableau de bord pour piloter l'ensemble de votre réseau, comparer la performance entre établissements, et <strong>prendre des décisions stratégiques éclairées</strong> plutôt que de naviguer dans des fichiers Excel dispersés.</p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-souverainete">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#06B6D4'}}><Lock size={30} /></div>
              <h3>Souveraineté des données et conformité garantie</h3>
            </div>
            <p>Toutes les données de votre réseau scolaire sont hébergées au Québec et au Canada par <strong>OVH Cloud</strong> (Montréal & Toronto). Aucune donnée n'est transférée à l'étranger.</p>
            <p>Les standards nutritionnels peuvent être définis au niveau du réseau et vérifiés automatiquement pour chaque établissement, assurant une <strong>conformité totale et traçable</strong> lors des audits internes ou externes.</p>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section problem-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Le défi des Directions d'établissement</h2>
            <p className="section-subtitle">
              Superviser plusieurs écoles sans visibilité centralisée est un casse-tête
            </p>
          </div>

          <div className="problems-grid">
            <div className="problem-card">
              <div className="problem-icon"><BarChart3 size={32} style={{color: '#84CC16'}} /></div>
              <h3>Données fragmentées</h3>
              <p>Chaque école utilise ses propres outils. Impossible d'avoir une vue d'ensemble cohérente.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><MessageSquare size={32} style={{color: '#84CC16'}} /></div>
              <h3>Communication dispersée</h3>
              <p>Emails, appels, Excel... Les informations se perdent entre les différents établissements.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><Shield size={32} style={{color: '#84CC16'}} /></div>
              <h3>Conformité incertaine</h3>
              <p>Difficile de s'assurer que tous les établissements respectent les mêmes standards nutritionnels.</p>
            </div>

            <div className="problem-card">
              <div className="problem-icon"><DollarSign size={32} style={{color: '#84CC16'}} /></div>
              <h3>Budgets opaques</h3>
              <p>Visibilité limitée sur les dépenses alimentaires consolidées et les opportunités d'optimisation.</p>
            </div>
          </div>

          <div className="solution-intro">
            <h3>EcoLunch centralise tout</h3>
            <p>Une vue unique pour piloter efficacement tous vos établissements</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Portail de Gouvernance Multi-Établissements</h2>
            <p className="section-subtitle">
              Supervisez, comparez, et optimisez l'alimentation de tout votre réseau
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Eye size={32} /></div>
              <h3>Vue consolidée temps réel</h3>
              <p>Dashboard unique affichant tous vos établissements. Participation, satisfaction, revenus : tout en un coup d'œil.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><BarChart3 size={32} /></div>
              <h3>Analyse comparative</h3>
              <p>Comparez la performance alimentaire entre établissements. Identifiez les meilleures pratiques et les points d'amélioration.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><FileText size={32} /></div>
              <h3>Rapports de portefeuille</h3>
              <p>Rapports consolidés automatiques : participation globale, satisfaction moyenne, dépenses totales. Exportables pour le conseil.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Conformité & Standards</h3>
              <p>Définissez les standards nutritionnels au niveau du réseau. Vérifiez automatiquement la conformité de chaque établissement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><DollarSign size={32} /></div>
              <h3>Gestion budgétaire</h3>
              <p>Visibilité complète sur les dépenses alimentaires par école et globales. Optimisez les contrats avec les traiteurs.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><TrendingUp size={32} /></div>
              <h3>Tendances & Prévisions</h3>
              <p>Analysez les tendances de participation et satisfaction. Prévisions basées sur l'historique pour planification budgétaire.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Supervision des acteurs</h3>
              <p>Vue sur tous les traiteurs, écoles, et volumes. Identifiez rapidement les problèmes avant qu'ils s'aggravent.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Alertes stratégiques</h3>
              <p>Notifications en cas de baisse significative de participation, problèmes récurrents, ou non-conformité détectée.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><GitBranch size={32} /></div>
              <h3>Communication descendante</h3>
              <p>Diffusez les directives et politiques à tous les établissements simultanément. Assurez l'alignement du réseau.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Governance Section */}
      <section className="section roi-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Gouvernance renforcée</h2>
            <p className="section-subtitle">
              Prenez des décisions éclairées avec des données fiables et consolidées
            </p>
          </div>

          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-metric">
                <Target size={40} />
                <h3>Objectifs mesurables</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Définissez des objectifs de participation, satisfaction, et conformité nutritionnelle au niveau du réseau. 
                Suivez l'atteinte de ces objectifs en temps réel pour chaque établissement.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <Award size={40} />
                <h3>Benchmarking interne</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Identifiez les écoles performantes et celles nécessitant du support. 
                Partagez les meilleures pratiques pour améliorer l'ensemble du réseau.
              </p>
            </div>

            <div className="roi-card">
              <div className="roi-metric">
                <CheckCircle size={40} />
                <h3>Audit & Traçabilité</h3>
              </div>
              <p style={{color: 'var(--color-text-secondary)', lineHeight: '1.8', marginTop: '1rem'}}>
                Historique complet de toutes les décisions, communications, et modifications. 
                Traçabilité parfaite pour audits internes ou externes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-directions">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(168, 85, 247, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>pour les Directions</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  Une phrase suffit pour tout contrôler
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Participation globale toutes écoles ce mois-ci</div>
              <div className="ecopulse-mini-cmd">Compare coûts par repas entre traiteurs</div>
              <div className="ecopulse-mini-cmd">Prévisions budget Q2</div>
              <div className="ecopulse-mini-cmd">Rapport annuel pour la commission</div>
            </div>
            <button onClick={() => navigate('/ecopulse')} className="ecopulse-mini-link">
              Découvrir toutes les commandes EcoPulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageDirections;